import type { ReactNode } from 'react';
import {
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/shared/ui/chakra/dialog';

/* Centered modal dialog on a dim backdrop. Chakra Dialog under the hood
   (focus trap, Escape + click-outside close, scroll lock). Kept as a
   thin `open/onClose/title` wrapper so every caller — the etap-23 CRUD
   pages, the provider token-rotation flow, the rules editor — stays
   unchanged.

   The positioner has no padding by default, so on a phone (viewport
   narrower than `maxWidth`) the card touches both screen edges. `offset`
   gives it breathing room below `md`; `maxH`+`overflowY` caps a tall form
   at 90dvh with internal scroll instead of running behind the on-screen
   keyboard or off the bottom of the screen. */

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Maximum width in px; defaults to 520 — enough for a two-column form. */
  maxWidth?: number;
}

export function Modal({ open, onClose, title, children, maxWidth = 520 }: Props) {
  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) onClose();
      }}
      placement="center"
    >
      <DialogContent
        data-testid="modal"
        maxW={`${maxWidth}px`}
        maxH="90dvh"
        overflowY="auto"
        offset={{ base: '3', md: '0' }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody pb="6">{children}</DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}
